import { useQuery, useMutation } from '@tanstack/react-query';
import { applicationInsights } from '../../../ApplicationInsights';
import { TemplatesApi, UsersApi, LoginApi } from '@apply/api';
import { UserAgreementType } from '@apply/constants';
import { handleAdminError, handleAdminSuccess } from '../../../common/SharedFunctions';
import { useWaitOverlayStore } from '@apply/components';
import store, { bfiUserActions, userActions } from '@apply/adminStore';
import { useNavigate } from 'react-router';

const templatesApi = new TemplatesApi.TemplatesApi(applicationInsights);
const usersApi = new UsersApi.UsersApi(applicationInsights);
const loginApi = new LoginApi.LoginApi(applicationInsights);

export function useUserAgreementHtmlQuery() {
    const { setWaiting } = useWaitOverlayStore();

    return useQuery({
        queryKey: ['userAgreementHtml'],
        queryFn: async () => {
            setWaiting(true, 'Retrieving User Agreement...');
            try {
                const response = await templatesApi.getUserAgreementHtml(UserAgreementType.ElectronicRecordsAndDisclosures);
                return response.statusCode === 200 ? response.result : '';
            } finally {
                setWaiting(false, '');
            }
        },
        staleTime: Infinity,
    });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useSaveUserAgreementMutation({ bfiUser, user }: any) {
    const { setWaiting } = useWaitOverlayStore();
    const { dispatch } = store;
    const navigate = useNavigate();
    const FONT_ID: string = '1';

    return useMutation({
        mutationFn: async () => {
            setWaiting(true, 'Saving consent...');

            try {
                // Get IP address
                let ipAddress = '';
                const ipResponse = await loginApi.getIpAddress();
                if (ipResponse.statusCode === 200) {
                    ipAddress = ipResponse.result;
                }

                const model = {
                    userId: bfiUser.userId,
                    fontId: parseInt(FONT_ID),
                    userAgreementTypeId: UserAgreementType.ElectronicRecordsAndDisclosures,
                    ipAddress,
                };

                return await usersApi.signUserAgreement(model);
            } finally {
                setWaiting(false, '');
            }
        },

        onSuccess: () => {
            handleAdminSuccess('Consent saved!');
            bfiUser.areTermsAccepted = true;
            bfiUser.requiresESign = false;
            dispatch(bfiUserActions.bfiUserUpdate(bfiUser));

            user.areTermsAccepted = true;
            user.requiresESign = false;
            dispatch(userActions.userUpdate(user));

            navigate('/referrals');
        },

        onError: (ex) => {
            handleAdminError(ex, 'An unexpected error occurred! Please try again.');
        },
    });
}
